// BTEditor.tsx
import React, { useEffect, useMemo, useRef } from 'react';
import { TextField } from '@mui/material';
import ReactQuill, { Quill } from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import '../../../css/quillstyles.css';
import BTBox from '../BTBox';
import BTButton from '../BTButton';
import { HorizontalLayout } from '../../common-layout';
import theme from '../../theme';
import { dialogStore } from '../../../store/DialogStore';
import { ImageActions } from '@xeger/quill-image-actions';
import { ImageFormats } from '@xeger/quill-image-formats';
import { useNavigate } from 'react-router-dom';
import CloseIconRound from '../../../assets/icons/close_icon_round.svg';
import FileIcon from '../../../assets/icons/file_icon.svg';

const Size = Quill.import('attributors/style/size');
Size.whitelist = [
    '8px', '9px', '10px', '11px', '12px', '13px', '14px', '15px', '16px', '17px', '18px', '19px', '20px',
    '21px', '22px', '24px', '26px', '28px', '30px', '32px', '34px', '36px', '38px', '40px',
    '42px', '44px', '46px', '48px', '50px', '52px', '56px', '60px', '64px', '68px', '72px', '76px', '80px', '88px', '96px'
];
Quill.register(Size, true);

Quill.register('modules/imageActions', ImageActions);
Quill.register('modules/imageFormats', ImageFormats);

export type BTEditorProps = {
    title: string;
    contents: string;
    selectedImage?: string | null;
    uploadedFiles: File[];
    uploadedFileUrls: string[];
    setTitle: (val: string) => void;
    setContents: (val: string) => void;
    setSelectedImage?: (val: string) => void;
    setUploadedFiles: (val: File[]) => void;
    setUploadedFileUrls: (val: string[]) => void;
    onSave: () => void;
    onCancel: () => void;
    /** 첨부파일 허용 범위: 기본 all(모든 파일). 'images' | 'documents' | 커스텀 accept 문자열 */
    attachmentAccept?: 'all' | 'images' | 'documents' | string;
};

const BTEditor = ({
    title,
    contents,
    selectedImage,
    uploadedFiles,
    uploadedFileUrls,
    setTitle,
    setContents,
    setSelectedImage,
    setUploadedFiles,
    setUploadedFileUrls,
    onSave,
    onCancel,
    attachmentAccept = 'all',
}: BTEditorProps) => {
    const navigate = useNavigate();
    const quillRef = useRef<ReactQuill>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const isResizingRef = useRef<boolean>(false);
    const [isImageEditing, setIsImageEditing] = React.useState(false);

    // 이미지의 width/height 보존 (Quill 로딩 전 전처리) - 한 번만 실행
    const initialContents = useMemo(() => {
        const html = contents;
        if (!html) return html;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // 1. span 안에 img가 있으면 span의 width/height를 img style로 이동
        const spans = doc.querySelectorAll('span[width], span[height]');
        const processedImages = new Set<HTMLImageElement>();

        spans.forEach((span) => {
            const img = span.querySelector('img');
            if (img) {
                const spanWidth = span.getAttribute('width');
                const spanHeight = span.getAttribute('height');
                const currentStyle = img.getAttribute('style') || '';

                // img의 style 파싱
                let styleObj: Record<string, string> = {};
                if (currentStyle) {
                    currentStyle.split(';').forEach(rule => {
                        const [key, value] = rule.split(':').map(s => s.trim());
                        if (key && value) styleObj[key] = value;
                    });
                }

                // span의 width/height를 img style로 추가 (우선순위 높음)
                if (spanWidth) {
                    styleObj['width'] = `${spanWidth}px`;
                }
                if (spanHeight) {
                    styleObj['height'] = `${spanHeight}px`;
                }

                // img style 재구성
                const newStyle = Object.entries(styleObj)
                    .filter(([_, value]) => value)
                    .map(([key, value]) => `${key}: ${value}`)
                    .join('; ');

                if (newStyle) {
                    img.setAttribute('style', newStyle);
                }

                // 처리된 이미지로 표시
                processedImages.add(img as HTMLImageElement);
            }
        });

        // 2. 나머지 이미지의 width/height 속성을 style로 변환
        const images = doc.querySelectorAll('img');
        images.forEach((img) => {
            // 이미 span에서 처리된 이미지는 스킵
            if (processedImages.has(img as HTMLImageElement)) {
                return;
            }

            const widthAttr = img.getAttribute('width');
            const heightAttr = img.getAttribute('height');
            const currentStyle = img.getAttribute('style') || '';

            // style 파싱
            let styleObj: Record<string, string> = {};
            if (currentStyle) {
                currentStyle.split(';').forEach(rule => {
                    const [key, value] = rule.split(':').map(s => s.trim());
                    if (key && value) styleObj[key] = value;
                });
            }

            // width/height 속성이 있으면 style로 변환
            if (widthAttr) {
                styleObj['width'] = `${widthAttr}px`;
            }
            if (heightAttr) {
                styleObj['height'] = `${heightAttr}px`;
            }

            // style 재구성
            const newStyle = Object.entries(styleObj)
                .filter(([_, value]) => value)
                .map(([key, value]) => `${key}: ${value}`)
                .join('; ');

            if (newStyle) {
                img.setAttribute('style', newStyle);
            }
        });

        return doc.body.innerHTML;
    }, []); // 빈 의존성 배열 - 마운트 시 한 번만 실행

    // 허용 MIME 타입 맵 (문서 전용 옵션 포함)
    const ACCEPT_MAP: Record<'all' | 'images' | 'documents', string> = {
        all: '*/*',
        images: 'image/*',
        documents: [
            'application/pdf',
            'application/msword',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'application/vnd.ms-powerpoint',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            'text/plain',
            'text/csv',
            'application/zip',
        ].join(','),
    };
    const resolvedAccept =
        attachmentAccept === 'all' || attachmentAccept === 'images' || attachmentAccept === 'documents'
            ? ACCEPT_MAP[attachmentAccept]
            : (attachmentAccept || '*/*'); // 커스텀 문자열 지원

    const colorPalette = [
        // 1행: 검정 → 흰색 (그레이 스케일)
        '#000000', '#1a1a1a', '#333333', '#4d4d4d', '#666666', '#808080', '#999999', '#b3b3b3', '#cccccc', '#ffffff',

        // 2행: 빨강 계열
        '#330000', '#660000', '#990000', '#cc0000', '#ff0000', '#ff3333', '#ff6666', '#ff9999', '#ffcccc', '#ffe6e6',

        // 3행: 주황 계열
        '#331a00', '#663300', '#994d00', '#cc6600', '#ff8000', '#ff9933', '#ffb266', '#ffcc99', '#ffe5cc', '#fff2e6',

        // 4행: 노랑 계열
        '#333300', '#666600', '#999900', '#cccc00', '#ffff00', '#ffff33', '#ffff66', '#ffff99', '#ffffcc', '#ffffe6',

        // 5행: 초록 계열
        '#003300', '#006600', '#009900', '#00cc00', '#00ff00', '#33ff33', '#66ff66', '#99ff99', '#ccffcc', '#e6ffe6',

        // 6행: 청록 계열
        '#003333', '#006666', '#009999', '#00cccc', '#00ffff', '#33ffff', '#66ffff', '#99ffff', '#ccffff', '#e6ffff',

        // 7행: 파랑 계열
        '#000033', '#000066', '#000099', '#0000cc', '#0000ff', '#3333ff', '#6666ff', '#9999ff', '#ccccff', '#e6e6ff',

        // 8행: 보라 계열
        '#330033', '#660066', '#990099', '#cc00cc', '#ff00ff', '#ff33ff', '#ff66ff', '#ff99ff', '#ffccff', '#ffe6ff',

        // 9행: 핑크 계열
        '#660033', '#990033', '#cc3366', '#ff6699', '#ff99cc', '#ffb2d1', '#ffccd9', '#ffe6e6', '#ffd6e6', '#ffe6f2',

        // 10행: 브라운/그레이 혼합 계열
        '#331a0d', '#66331a', '#994d26', '#cc6640', '#ff8066', '#ff9980', '#ffb299', '#ffccb3', '#ffe5cc', '#fff2e6',

        '#00000000', // 투명색 추가
    ];


    const modules = {
        toolbar: {
            container: [
                [{ size: Size.whitelist }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ align: [] }],
                [{ color: colorPalette }, { background: colorPalette }],
                ['link', 'image'],
                [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
                ['blockquote', 'code-block'],
                ['clean'],
                [{ script: 'sub' }, { script: 'super' }],
            ],
            handlers: {},
        },
        imageActions: {},
        imageFormats: {},
    };

    const formats = [
        'size',
        'bold',
        'italic',
        'underline',
        'strike',
        'blockquote',
        'code-block',
        'list',
        'bullet',
        'indent',
        'script',
        'align',
        'link',
        'color',
        'background',
        'image',
        'float',
        'width',
        'height',
    ];

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (files) {
            const newFiles = Array.from(files);
            const filtered = newFiles.filter((newFile) =>
                !uploadedFiles.some(
                    (existing) =>
                        existing.name === newFile.name && existing.size === newFile.size
                )
            );
            setUploadedFiles([...uploadedFiles, ...filtered]);
        }
    };

    const handleFileRemove = (file: File) => {
        setUploadedFiles(
            uploadedFiles.filter(
                (f) => f.name !== file.name || f.size !== file.size
            )
        );
    };

    const handleFileUrlRemove = (url: string) => {
        setUploadedFileUrls(uploadedFileUrls.filter((f) => f !== url));
    };

    const attachedFiles = useMemo(() => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(contents, 'text/html');
        const images = Array.from(doc.querySelectorAll('img'))
            .map((img) => img.getAttribute('src'))
            .filter((src): src is string => !!src);
        return images;
    }, [contents]);

    useEffect(() => {
        if (!setSelectedImage) return;
        if (attachedFiles.length === 0) return;

        // 대표 이미지가 비어있거나, 현재 본문 이미지 목록에 포함되지 않으면 첫 이미지를 기본 선택
        if (!selectedImage || !attachedFiles.includes(selectedImage)) {
            setSelectedImage(attachedFiles[0]);
        }
    }, [attachedFiles, selectedImage]);

    const checkRequiredInputs = (): boolean => {
        if (!title.trim()) {
            dialogStore.showDialog({
                title: '입력 필요',
                message: '제목을 입력해주세요.',
                onConfirm: () => { }
            });
            return false;
        }
        if (!contents.trim() || contents === '<p><br></p>') {
            dialogStore.showDialog({
                title: '입력 필요',
                message: '내용을 입력해주세요.',
                onConfirm: () => { }
            });
            return false;
        }
        return true;
    };

    const handleSave = () => {
        // 저장 전에 현재 에디터 내용을 가져와서 업데이트
        if (quillRef.current) {
            const quill = quillRef.current.getEditor();
            setContents(quill.root.innerHTML);
        }

        if (!checkRequiredInputs()) return;

        // setContents가 비동기이므로 약간의 딜레이 후 저장
        setTimeout(() => {
            onSave();
        }, 50);
    };

    const handleCancel = () => {
        dialogStore.showDialog({
            title: '확인',
            message: '정말 그만두시겠습니까?',
            onConfirm: () => {
                onCancel();
            },
            onCancel: () => {
                // 취소 다이얼로그에서 취소 버튼 클릭
            }
        });
    }

    useEffect(() => {
        if (!quillRef.current) return;
        const quill = quillRef.current.getEditor();

        // 전체 에디터 초기값 폰트 사이즈 16px
        quill.format('size', '16px');

        // 드롭다운 라벨도 강제로 16px
        const sizePicker = document.querySelector('.ql-picker.ql-size .ql-picker-label') as HTMLElement;
        if (sizePicker) sizePicker.setAttribute('data-value', '16px');

        // Quill 로딩 완료 후 즉시 전처리된 HTML 적용
        requestAnimationFrame(() => {
            if (initialContents) {
                quill.root.innerHTML = initialContents;
            }
        });

        // 부모 스크롤 컨테이너 찾기
        let scrollContainer: HTMLElement | null = quill.root.parentElement;
        while (scrollContainer) {
            const style = window.getComputedStyle(scrollContainer);
            if (style.overflowY === 'auto' || style.overflowY === 'scroll') {
                break;
            }
            scrollContainer = scrollContainer.parentElement;
        }

        if (!scrollContainer) return;

        let savedScrollTop = 0;
        let isToolbarActive = false;

        // 스크롤 강제 고정
        const preventScroll = (e: Event) => {
            if (isToolbarActive) {
                e.preventDefault();
                scrollContainer!.scrollTop = savedScrollTop;
            }
        };

        scrollContainer.addEventListener('scroll', preventScroll, { passive: false });

        // 툴바 클릭 시 스크롤 고정
        const toolbar = document.querySelector('.ql-toolbar');
        if (toolbar) {
            const handleToolbarMouseDown = () => {
                savedScrollTop = scrollContainer!.scrollTop;
                isToolbarActive = true;
            };

            const handleToolbarMouseUp = () => {
                setTimeout(() => {
                    isToolbarActive = false;
                }, 300);
            };

            toolbar.addEventListener('mousedown', handleToolbarMouseDown);
            document.addEventListener('mouseup', handleToolbarMouseUp);

            return () => {
                scrollContainer!.removeEventListener('scroll', preventScroll);
                toolbar.removeEventListener('mousedown', handleToolbarMouseDown);
                document.removeEventListener('mouseup', handleToolbarMouseUp);
            };
        }

        return () => {
            scrollContainer!.removeEventListener('scroll', preventScroll);
        };
    }, []);

    useEffect(() => {
        const quill = quillRef.current?.getEditor();
        if (!quill) return;

        const handleClick = () => {
            const tracker = document.querySelector('.image-actions__resize-handle');
            const isEditing = !!tracker;
            setIsImageEditing(isEditing);
        };

        quill.root.addEventListener('click', handleClick);

        return () => {
            quill.root.removeEventListener('click', handleClick);
        };
    }, []);

    useEffect(() => {
        const toolbar = document.querySelector('.ql-toolbar') as HTMLElement;
        if (!toolbar) return;

        if (isImageEditing) {
            toolbar.style.pointerEvents = 'none'; // 클릭 막기
            toolbar.style.opacity = '0.5';        // 딤 처리
        } else {
            toolbar.style.pointerEvents = 'auto'; // 클릭 가능
            toolbar.style.opacity = '1';          // 정상 표시
        }
    }, [isImageEditing]);

    useEffect(() => {
        if (!quillRef.current) return;
        const quill = quillRef.current.getEditor();
        const root = quill.root;

        const handleMouseUp = () => {
            const hasTracker = !!document.querySelector('.image-actions__resize-handle');
            if (!hasTracker) return;

            setTimeout(() => {
                const root = quillRef.current?.getEditor().root;
                if (!root) return;

                // 1) root 안의 모든 img 탐색
                const imgs = root.querySelectorAll("img");

                imgs.forEach((img) => {
                    const parent = img.closest("span");
                    if (!parent) return;

                    // 이미지의 width/height 가져오기
                    const w =
                        img.style.width?.replace("px", "") ||
                        img.getAttribute("width");
                    const h =
                        img.style.height?.replace("px", "") ||
                        img.getAttribute("height");

                    if (!w || !h) return;

                    // 2) span에 width, height 속성 직접 적용
                    parent.setAttribute("width", w);
                    parent.setAttribute("height", h);
                });

                // 3) 최종 innerHTML 저장
                setContents(root.innerHTML);
            }, 100);
        };

        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, []);

    return (
        <div style={{ width: '100%' }}>
            <form noValidate autoComplete="off" onSubmit={(e) => e.preventDefault()}>
                {/* 제목 */}
                <div
                    style={{
                        display: "flex",
                        gap: "16px",
                        alignItems: "center",
                        marginTop: "8px",
                        fontSize: "14px",
                        color: "#6C757D",
                        width: "100%"
                    }}
                >
                    <div
                        style={{
                            fontWeight: "700",
                            minWidth: "48px",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                        }}
                    >
                        제목
                    </div>
                    <div style={{ flex: 1 }}>
                        <TextField
                            label="제목"
                            variant="outlined"
                            fullWidth
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                        />
                    </div>
                </div>

                {/* 본문 */}
                <div
                    style={{
                        display: "flex",
                        gap: "16px",
                        alignItems: "flex-start",
                        marginTop: "8px",
                        fontSize: "14px",
                        color: "#6C757D",
                        width: "100%",
                    }}
                >
                    <div
                        style={{
                            fontWeight: "700",
                            minWidth: "48px",
                            whiteSpace: "nowrap",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            lineHeight: "28px",
                        }}
                    >
                        본문
                    </div>
                    <div style={{ flex: 1 }}>
                        <ReactQuill
                            ref={quillRef}
                            defaultValue={contents || '<p style="font-size:16px;"><br></p>'}
                            onChange={(value) => {
                                // 이미지 리사이즈 중에는 onChange 무시
                                if (!isResizingRef.current) {
                                    setContents(value);
                                }
                            }}
                            theme="snow"
                            modules={modules}
                            formats={formats}
                            style={{ height: '100%' }}
                        />
                    </div>
                </div>

                <BTBox height="16px" />

                {/* 썸네일 이미지 리스트 */}
                {attachedFiles.length > 0 && setSelectedImage && (
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                        {/* 제목 */}
                        <div
                            style={{
                                minWidth: '64px',
                                fontSize: '14px',
                                color: '#6C757D',
                                lineHeight: '28px', // 제목과 텍스트 높이 정렬
                            }}
                        >
                            <b>첨부 이미지</b>
                        </div>

                        {/* 파일 목록 */}
                        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                            {attachedFiles.map((file, index) => {
                                const isSelected = selectedImage === file;

                                return (
                                    <div
                                        key={index}
                                        onClick={() => setSelectedImage(file)}
                                        style={{
                                            width: '100px',
                                            height: '100px',
                                            border: isSelected ? '3px solid #14B446' : '1px solid #ccc',
                                            borderRadius: '6px',
                                            overflow: 'hidden',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            backgroundColor: '#fafafa',
                                            cursor: 'pointer',
                                            boxShadow: isSelected ? '0 0 8px rgba(20, 180, 70, 0.5)' : 'none',
                                            transition: '0.2s',
                                        }}
                                    >
                                        <img
                                            src={file}
                                            alt={`썸네일${index + 1}`}
                                            style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'cover' }}
                                        />
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                <BTBox height="16px" />

                {/* 파일 선택 버튼 (숨긴 input + BTButton) */}
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                    {/* 제목 */}
                    <div
                        style={{
                            minWidth: '64px',
                            fontSize: '14px',
                            color: '#6C757D',
                            lineHeight: '28px', // 제목과 텍스트 높이 정렬
                        }}
                    >
                        <b>첨부파일</b>
                    </div>

                    {/* 파일 목록 */}
                    <div style={{ flex: 1, display: "flex", alignItems: "center", gap: 12 }}>
                        <input
                            ref={fileInputRef}
                            id="patent-edit-file-input"
                            type="file"
                            accept={resolvedAccept}
                            multiple
                            onChange={handleFileChange}
                            style={{
                                position: "absolute",
                                width: 1,
                                height: 1,
                                padding: 0,
                                margin: -1,
                                overflow: "hidden",
                                clip: "rect(0 0 0 0)",
                                whiteSpace: "nowrap",
                                border: 0,
                            }}
                        />
                        <BTButton
                            width="auto"
                            height="40px"
                            color="#E7E7E7"
                            textColor="#4E6173"
                            onClick={() => fileInputRef.current?.click()}
                            sx={{ px: 2, display: "inline-flex", alignItems: "center", gap: 1, fontSize: "14px" }}
                            aria-label="파일 선택"
                        >
                            <img src={FileIcon} />
                            <span>파일 선택</span>
                        </BTButton>
                    </div>
                </div>
                <BTBox height="16px" />

                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                    {/* 제목 */}
                    <div
                        style={{
                            minWidth: '64px',
                            fontSize: '14px',
                            color: '#6C757D',
                            lineHeight: '28px', // 제목과 텍스트 높이 정렬
                        }}
                    >
                        <b>첨부된 파일목록</b>
                    </div>

                    {/* 파일 목록 */}
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '8px',
                            flex: 1,
                        }}
                    >
                        <ul style={{ marginLeft: 0, paddingLeft: 0, listStyle: 'none' }}>
                            {uploadedFileUrls.map((url, index) => {
                                const cleanUrl = url.split('?')[0];
                                const filename = decodeURIComponent(cleanUrl.split('/').pop() || `첨부파일${index + 1}`);
                                return (
                                    <li key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                                        <span style={{ marginRight: '8px', color: '#4E6173' }}>{filename}</span>
                                        <img src={CloseIconRound} onClick={() => handleFileUrlRemove(url)} />

                                    </li>
                                );
                            })}
                            {uploadedFiles.map((file, index) => (
                                <li key={index} style={{ display: 'flex', alignItems: 'center', marginBottom: '2px' }}>
                                    <span style={{ marginRight: '8px', color: '#4E6173' }}>{file.name}</span>
                                    <img src={CloseIconRound} onClick={() => handleFileRemove(file)} />
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>

                <BTBox height='36px' />

                <HorizontalLayout style={{ justifyContent: 'flex-end' }}>
                    <BTButton color={theme.colors.bbTech} textColor="white" onClick={handleSave}>저장</BTButton>
                    <BTBox width="16px" />
                    <BTButton onClick={handleCancel}>취소</BTButton>
                </HorizontalLayout>
            </form>
        </div>
    );
};

export default BTEditor;
